import React, {useState} from 'react';
import produce from 'immer';
import isEmpty from 'lodash/isEmpty';

import {SkeletonListReact} from 'common/components/skeleton-list';
import {SkeletonListRowSize} from 'common/components/skeleton-list/types';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {EntryRowProps} from 'common/query-builder/query-entry-section/row-types/types';
import {
  Action,
  Entry,
  EntrySectionUpdates,
  EntryType,
  EntryWithDisplay,
  MetricEntry,
  QueryMetricEntry,
  QuerySavedMetricEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {ModelStatus} from 'common/util/model-resource/model-resource';
// eslint-disable-next-line import/no-cycle
import {QueryEntrySectionReact} from 'common/query-builder/query-entry-section';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {MetricUnitsButton, MetricUnitsSelectorDropdown} from 'common/query-builder/chart-customization/metric-units';
import {useTranslation} from 'common/i18n/hooks';
import {BaseEntryRow} from 'common/query-builder/query-entry-section/row-types/base';
import {EditControls} from 'common/query-builder/query-entry-section/components/edit-controls';
import {CallingLocation} from 'common/query-builder/chart-customization/tracking';
import {getEditCapability} from 'common/report/util/saved-entities';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import {RowSelector} from 'common/query-builder/query-entry-section/components/selector';
import {MenuType, MultiMetricsSectionItem} from 'common/query-builder/query-block/multi-metrics-menu/types';
import {ButtonReact} from 'common/components/button';
import {setEntryVisibility, toggleExpandedRow} from 'common/query-builder/query-entry-section/row-types/row-util';
import type {BehaviorMetricDisplay, BehaviorShowClause, MetricMeasurement} from 'common/report/insights/util/types';
import {getReferencedMetricsFromEntries} from 'common/query-builder/query-entry-section/row-types/formula-entry/utils';
import {getEntries} from 'common/report/insights/util/events';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {
  ControlBarAction,
  ControlBarActionItems,
  ControlBarActionValue,
} from 'common/query-builder/query-entry-section/constants';
import {DisplaySize} from 'common/components/tooltip/types';

import {
  getHydratedShowClause,
  getMetricModel,
  isModifiedQuerySavedMetricEntry,
  MetricEntryUpdate,
} from '../metric-entry-util';
import {saveMetric} from '../behavior-utils';
import {RealMetricsRequest, SavedMetric} from './types';

// TODO: Need to fix typing of ControlBarActionItemsType
const controlBarActionItems = ControlBarActionItems as any;

/**
 * In cases where there are local changes defer to those otherwise default
 * to the saved metric definition.
 */
const getFinalMetricWithLocalChanges = (
  metric: SavedMetric,
  clause: BehaviorShowClause,
  localDisplay: BehaviorMetricDisplay | undefined,
  behaviorsResource: BehaviorsResource,
): BehaviorShowClause => {
  const finalClause: BehaviorShowClause = {
    behavior: !isEmpty(clause.behavior) ? clause.behavior : metric.definition.behavior,
    measurement: !isEmpty(clause.measurement) ? clause.measurement : metric.definition.measurement,
    display: {
      ...(!isEmpty(clause.display) ? clause.display : metric.definition.display),
      ...localDisplay,
    },
    isExpanded: true,
  };
  // Hydrate saved behaviors
  // TODO: Should we just do this in `getEntries`?
  return getHydratedShowClause(finalClause, behaviorsResource);
};

const getUpdatedSavedMetricEntry = (
  entry: QuerySavedMetricEntry,
  modifiedParams: MetricEntryUpdate,
): QuerySavedMetricEntry => {
  return produce(entry, (draft) => {
    const updatedEvent: BehaviorShowClause = {
      ...entry.event,
      type: TopLevelMetricType.Metric,
    };
    if (modifiedParams.behavior) {
      updatedEvent.behavior = {
        ...updatedEvent.behavior,
        ...modifiedParams.behavior,
      };
    }
    if (modifiedParams.measurement) {
      updatedEvent.measurement = {
        ...updatedEvent.measurement,
        ...(modifiedParams.measurement as MetricMeasurement),
      };
    }
    draft.isExpanded = true;
    draft.isHidden = entry.isHidden;
    draft.event = updatedEvent;
    if (modifiedParams.display) {
      draft.display = {
        ...draft.display,
        ...modifiedParams.display,
      };
    }
    return draft;
  });
};

export function SavedMetricEntryRow(props: EntryRowProps<QuerySavedMetricEntry>) {
  const behaviors = useBehaviors();
  const currentProject = useCurrentProject();
  const metrics = useMetrics();
  const i18n = useTranslation();
  const [metricsUnitOpen, setMetricsUnitOpen] = useState(false);
  const [entryEl, setEntryEl] = useState<HTMLDivElement | null>(null);

  const {t} = i18n;
  const model = getMetricModel(props.entry.event, metrics);
  const metric = model?.value;
  // Make sure we merge in local state with saved db state.
  const innerMetricShowClause: BehaviorShowClause | null = metric
    ? getFinalMetricWithLocalChanges(metric, props.entry.event, props.entry.display, behaviors)
    : null;
  const skeleton = (
    <div className='mp-query-entry-row for-formula-definition'>
      <SkeletonListReact numRows={4} rowSize={SkeletonListRowSize.Large} className='mp-formulas-menu-skeleton-list' />
    </div>
  );

  if (!metric || !innerMetricShowClause) {
    return skeleton;
  }

  const innerMetricEntry = getEntries({showClauses: [innerMetricShowClause]})[0];
  const isLoading = model ? model.status === ModelStatus.Loading : true;
  const {isExpanded} = props.entry;

  // TODO state
  const isSaving = false;

  const canEdit = getEditCapability(metric).enableEdit;
  const isModified = metric && innerMetricShowClause ? isModifiedQuerySavedMetricEntry(props.entry, metric) : false;
  const canSave = isModified;

  if (isLoading || !innerMetricShowClause) {
    return skeleton;
  }

  const querySectionEntries = [];
  if (innerMetricEntry) {
    querySectionEntries.push(innerMetricEntry);
  }

  const onReset = () => {
    const updatedEntry: QuerySavedMetricEntry = {
      type: MetricType.SavedMetric,
      event: {
        id: metric.id,
        type: TopLevelMetricType.Metric,
        behavior: {},
        measurement: {} as MetricMeasurement,
      },
      isExpanded: true,
    };
    props.updateEntry(Action.Edit, updatedEntry, {requery: true});
  };

  const handleSaveModifications = async (saveAsNew = false) => {
    if (!innerMetricShowClause) {
      return;
    }

    const metricEntity: RealMetricsRequest = {
      definition: {
        type: TopLevelMetricType.Metric,
        behavior: innerMetricShowClause.behavior,
        measurement: innerMetricShowClause.measurement,
        display: props.entry.display,
      },
      name: metric.name,
      type: TopLevelMetricType.Metric,
    };
    await saveMetric({
      metricEntity,
      metrics,
      i18n,
      currentProject,
      ...(saveAsNew ? {} : {metricEntityId: metric.id}),
    });
    onReset();
  };

  const updateMetricDisplay = (entry: EntryWithDisplay) => {
    const updatedEntry = getUpdatedSavedMetricEntry(props.entry, {display: entry.display});
    props.updateEntry(Action.Edit, updatedEntry, {requery: true});
  };

  const onInnerMetricsUpdate = (updates: EntrySectionUpdates<Entry>) => {
    let updatedEntry: QuerySavedMetricEntry | null = null;
    // Ignore updates that collapse/expand inner metric.
    // TODO: Can we just disable this in the UI so it never happens?
    if (!updates.entries.length) {
      return;
    }
    const convertedMetrics = getReferencedMetricsFromEntries(updates.entries as Array<QueryMetricEntry | MetricEntry>);

    if (!convertedMetrics.length) {
      return;
    }
    updatedEntry = getUpdatedSavedMetricEntry(props.entry, convertedMetrics[0]);
    if (!updatedEntry) {
      return;
    }
    // The contents of a saved metric are not collapsible.
    updatedEntry.isExpanded = true;

    props.updateEntry(Action.Edit, updatedEntry, {requery: true});
  };

  const actionsRenderer = () => {
    // Always show a modified metric so user can see underlying changes.
    if (isModified && isExpanded) {
      return null;
    }
    return (
      <ButtonReact
        className='expand-control'
        icon={isExpanded ? `collapse-diagonal` : `expand`}
        theme={ButtonTheme.Tertiary}
        size={ButtonSize.Medium}
        tooltip={{
          text: isExpanded ? t(`query-builder.actions.collapse`) : t(`query-builder.actions.expand`),
        }}
        onClick={() => toggleExpandedRow(props)}
      />
    );
  };

  /**
   * Turns this entry into an empty inline entry.
   */
  const handleCreateInline = (menuType: MenuType) => {
    props.onEntryRowCreate(
      {
        type: SelectorTypeDefinitions.Metrics,
        event: null,
        filters: [],
      },
      menuType,
    );
  };

  const handleSelectMultiMetrics = (sectionItem: MultiMetricsSectionItem) => {
    props.onEntryRowSelect(
      {
        type: SelectorTypeDefinitions.Metrics,
        event: null,
        filters: [],
      },
      props.entry,
      sectionItem,
      props.index,
    );
  };

  const handleControlSelect = (value: ControlBarActionValue) => {
    switch (value.action) {
      case ControlBarAction.SaveMetric: {
        handleSaveModifications(false);
        break;
      }
      case ControlBarAction.Show:
      case ControlBarAction.Hide:
        setEntryVisibility(props, value.action === ControlBarAction.Show);
        break;
      default:
        props.onControlSelect(value);
    }
  };

  const selectorRenderer = () => (
    <RowSelector
      {...props}
      contextPanelParentElement={entryEl}
      isPlaceholder={false}
      selected={{
        menuType: MenuType.SavedMetric,
        value: metric,
      }}
      selectorType={SelectorTypeDefinitions.Metrics}
      onSelect={(sectionItem: MultiMetricsSectionItem) => handleSelectMultiMetrics(sectionItem)}
      onCreate={(ev: {detail: {item: MenuType}}) => handleCreateInline(ev.detail.item)}
    />
  );

  const renderEditControls = () => {
    const metricUnitsButton = (
      <MetricUnitsButton
        insideFooter
        style={{width: `24px`}}
        size={ButtonSize.Medium}
        entry={props.entry}
        updateOpen={() => {
          setMetricsUnitOpen((x) => !x);
          props.onMetricUnitSelectorDropdownStateChanged(!props.isMetricUnitSelectorDropdownOpen);
        }}
      />
    );

    return (
      <>
        <EditControls
          resetTooltip={t(`query-builder.metrics.reset-tooltip`)}
          isSaving={isSaving}
          isSaved
          canSave={canSave}
          canEdit={canEdit}
          metricUnitsSelector={metricUnitsButton}
          canReset={isModified}
          onReset={onReset}
          entityType={t(`query-builder.metrics.metric`)}
          onSave={() => handleSaveModifications(false)}
          onClone={() => handleSaveModifications(true)}
        />
        <MetricUnitsSelectorDropdown
          callingLocation={CallingLocation.Metric}
          chartType={props.currentChartType}
          entry={props.entry}
          setEntry={(entry) => updateMetricDisplay(entry)}
          open={props.isMetricUnitSelectorDropdownOpen && metricsUnitOpen}
          setOpen={(open) => {
            props.onMetricUnitSelectorDropdownStateChanged(open);
            setMetricsUnitOpen(open);
          }}
        />
      </>
    );
  };

  const queryEntrySection = (
    <QueryEntrySectionReact
      elref='subEntry'
      className='sub-entry'
      {...props.nestedSectionParams}
      showDefaultMetricSelector={false}
      disableAddEntry
      disableCollapse
      addEntryMenuWidth={130}
      enableEventIcon
      entryType={EntryType.Event}
      showTopEvents={false}
      dateRange={props.sectionProps.dateRange}
      dateUnit={props.sectionProps.dateUnit}
      isNestedInSavedMetric
      isReferencedMetrics
      metricsMode
      excludedMetricsRowTypes={[MetricType.Formula, MetricType.SavedMetric]}
      rowIndex={props.index}
      rowDraggable
      trackProperties={{...(props.nestedSectionParams.trackProperties || {}), sectionName: `referenced-metrics`}}
      entries={querySectionEntries}
      onUpdate={onInnerMetricsUpdate}
    />
  );

  const suffixRenderer = () => {
    if (!isExpanded) {
      return null;
    }
    return (
      <div className='mp-query-entry-row for-saved-metric-definition'>
        {queryEntrySection}
        {renderEditControls()}
      </div>
    );
  };

  return (
    <>
      <BaseEntryRow
        {...props}
        ref={setEntryEl}
        isPlaceholder={false}
        onControlSelect={(v) => handleControlSelect(v)}
        overflowControls={[
          {
            ...controlBarActionItems[ControlBarAction.SaveMetric],
            ...(!canSave && {
              tooltip: {
                text: t(`query-builder.metrics.no-unsaved-changes`),
                size: DisplaySize.Medium,
              },
              isDisabled: true,
            }),
          },
          controlBarActionItems[props.entry.isHidden ? ControlBarAction.Show : ControlBarAction.Hide],
          controlBarActionItems[ControlBarAction.Duplicate],
          controlBarActionItems[ControlBarAction.Delete],
        ]}
        renderActions={actionsRenderer}
        renderSelector={selectorRenderer}
        renderSuffix={suffixRenderer}
        showDefaultMetricUnitSelector={false}
      />
    </>
  );
}
